import React, { useCallback, useMemo } from 'react';
import { Components, ExtendedComponentSchema } from 'formiojs';
import {
  FormioBuilderInfo,
  EhsFormioReactProps,
  EhsFormioReactComponent,
  FormioBuilderSchema
} from '../';
import { DateInput, DateTimeInput } from '../../../form';
import { intl } from '../../../../lib';
import { LanguageProperty } from '../../../generated/LanguageProperty';
import { Component } from '@formio/react';

//START-EHS-REACT-FORMIO-DEFINITION---------------------------------------------
export type FormioDateTimePickerConstructor = {
  timepicker: boolean;
};
export type FormioDateTimePickerSettings = {
  timepicker: boolean;
};
export const FormioDateTimePickerType = 'ehsdatetimepicker';
export class EhsFormioDateTimePicker extends EhsFormioReactComponent<
  FormioDateTimePickerSettings,
  FormioDateTimePickerConstructor
> {
  /**
   * defines the default schema of the custom component
   * https://help.form.io/developers/custom-components#component-methods
   */
  static schema(
    extend?: FormioBuilderSchema
  ): ExtendedComponentSchema<FormioBuilderSchema> {
    return Components.components.field.schema({
      type: FormioDateTimePickerType,
      label: 'common.date',
      key: FormioDateTimePickerType,
      timepicker: false,
      ...extend
    });
  }
  /**
   * defines how the component will look in the Form Editor (Builder)
   * https://help.form.io/developers/custom-components#component-methods
   */
  static get builderInfo(): FormioBuilderInfo {
    return {
      title: 'Ehs-DateTimepicker',
      group: 'advanced',
      icon: 'calendar',
      documentation: '/developers/custom-components',
      weight: 40,
      schema: EhsFormioDateTimePicker.schema()
    };
  }

  /**
   * defines the fields available in the Editor/Builder
   * https://help.form.io/developers/form-builder#overriding-behavior
   */
  static editForm() {
    return {
      components: [
        {
          type: 'textfield',
          key: 'label',
          label: 'label',
          tooltip: 'Please add only valid translation keys'
        },
        {
          type: 'checkbox',
          input: true,
          key: 'timepicker',
          label: 'Add Time',
          tooltip: 'Show (Date- &) Time input .',
          weight: 30
        }
      ]
    };
  }

  //Begin Non static definitions-------------------------------------
  private ehsTimepicker = false;

  extractFields(component: Component<FormioDateTimePickerConstructor>): void {
    this.ehsTimepicker = component.timepicker;
  }

  attachEhsReact(): [
    (props: EhsFormioReactProps<FormioDateTimePickerSettings>) => JSX.Element,
    FormioDateTimePickerSettings
  ] {
    return [DatePickerCmp, { timepicker: this.ehsTimepicker }];
  }
}
//END-EHS-REACT-FORMIO-DEFINITION-----------------------------------------------

/**
 * The Logic for the Formio-Component.
 *
 * @param params formio {@link FormioDateTimePickerSettings} properties
 */
function DatePickerCmp({
  value,
  onChange,
  label,
  readOnly,
  settings
}: EhsFormioReactProps<FormioDateTimePickerSettings>): JSX.Element {
  const onChangeWrapper = useCallback(
    (selectedDates: Date | null): void => {
      onChange(selectedDates?.toISOString() ?? '');
    },
    [onChange]
  );
  const wrapperValue = useMemo(() => {
    return (value ?? '') == '' ? null : new Date(value);
  }, [value]);
  return useMemo(() => {
    if (settings.timepicker) {
      return (
        <DateTimeInput
          onChange={onChangeWrapper}
          value={wrapperValue}
          label={intl(label as LanguageProperty)}
          disabled={readOnly}
        />
      );
    } else {
      return (
        <DateInput
          onChange={onChangeWrapper}
          value={wrapperValue}
          label={intl(label as LanguageProperty)}
          disabled={readOnly}
        />
      );
    }
  }, [label, onChangeWrapper, readOnly, settings.timepicker, wrapperValue]);
}
